import React, { useEffect, useState } from 'react';
import {Table, DatePicker, Space, Button, message, Select, Input, Modal} from 'antd';
import {UndoOutlined, SaveOutlined, FileAddOutlined} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import moment from 'moment';
import dayjs, {Dayjs} from "dayjs";
import {getConfig} from "../../../config";
import {useProjectContext} from "../../../context/ProjectContext";
import {useDirtyContext} from "../../../context/DirtyContext";
import {ViewType} from "../../../types/ViewType";
import locale from 'antd/es/date-picker/locale/de_DE';
import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.locale('de');
dayjs.extend(isoWeek);

const { Option } = Select;
const { API_BASE_URL } = getConfig();

interface OGLConfigurationProps {
    selectedProject?: string;
    oglCodes?: OGLCode[];
    setOglCodes?: (data: OGLCode[]) => void;
    viewType?: ViewType;
    handleFinalStep?: () => void;
}

const OGLConfiguration: React.FC<OGLConfigurationProps> = ({ selectedProject, oglCodes, setOglCodes, handleFinalStep, viewType }) => {
    const [data, setData] = useState<OGLCode[]>([]);
    const [originalData, setOriginalData] = useState<OGLCode[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { selectedProject: contextProject } = useProjectContext();
    const activeProject = selectedProject || contextProject;
    const { isDirty, setDirty } = useDirtyContext();
    const [isSavingNewOGL, setIsSavingNewOGL] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [newOGL, setNewOGL] = useState<OGLCode>({
        code: '',
        ogl: '',
        description: '',
        moveInDate: null,
        scaffoldingDate: null,
        preAssemblyDateOutside: null,
        preAssemblyDateInside: null,
        measureInsideDate: null,
        measureOutsideDate: null,
        measureSurroundingsDate: null,
    });

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get<OGLCode[]>(
                `${API_BASE_URL}/getNetworkPlanOGLCodes?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${activeProject}`
            );
            setData(response.data);
            setOriginalData(response.data);
            setOglCodes?.(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            message.error('Fehler beim Laden der Daten.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!activeProject) return;
        fetchData();
    }, [activeProject]);


    function handleCreateNewOGL() {
        setNewOGL({
            code: '',
            ogl: '',
            description: '',
            moveInDate: null,
            scaffoldingDate: null,
            preAssemblyDateOutside: null,
            preAssemblyDateInside: null,
            measureInsideDate: null,
            measureOutsideDate: null,
            measureSurroundingsDate: null,
        });
        setIsModalVisible(true);
    }


    const hasChanges = (): boolean => {
        return data.some((record, index) => {
            const original = originalData[index];
            return original && Object.keys(record).some(key => record[key as keyof OGLCode] !== original[key as keyof OGLCode]);
        });
    };

    const isCellChanged = (record: OGLCode, key: keyof OGLCode): boolean => {
        const original = originalData.find(item => item.ogl === record.ogl);
        return original ? original[key] !== record[key] : false;
    };

    const handleDateChange = (date: Dayjs | null, record: OGLCode, key: keyof OGLCode) => {
        setDirty(true);
        const updatedData = data.map(item =>
            item.ogl === record.ogl
                ? { ...item, [key]: date ? date.format('DD.MM.YYYY') : null }
                : item
        );
        setData(updatedData);
        if (setOglCodes) {
            setOglCodes([...updatedData]);
        }
    };

    const handleReset = async () => {
        if (!activeProject) return;
        setDirty(false);
        await fetchData();
        message.success('Bauabschnitt-Daten erfolgreich zurückgesetzt.');
    };

    const handleSave = async () => {
        setIsLoading(true);
        setDirty(false);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/saveChangedNetworkPlanOGLCodes`,
                { projectNo: activeProject, data }
            );
            if (response.status === 200) {
                setOriginalData([...data]); // Update original data after successful save
                message.success('Daten erfolgreich gespeichert.');
            } else {
                throw new Error('Speichern fehlgeschlagen.');
            }
        } catch (error) {
            console.error('Error saving data:', error);
            message.error('Fehler beim Speichern der Daten.');
        } finally {
            setIsLoading(false);
        }
    };

    const isOGLFormValid = (): boolean => {
        const hasOGL = !!newOGL.ogl?.trim();
        const hasDescription = !!newOGL.description?.trim();
        const hasMoveInDate = !!newOGL.moveInDate;
        const hasScaffoldingDate = !!newOGL.scaffoldingDate;
        return hasOGL && hasDescription && hasMoveInDate && hasScaffoldingDate;
    };


    const renderCell = (
        value: any,
        record: OGLCode,
        key: keyof OGLCode
    ) => (
        <div
            style={{
                backgroundColor: isCellChanged(record, key) ? '#d9f7be' : 'transparent',
                padding: '4px',
                borderRadius: '4px',
            }}
        >
            <DatePicker
                placeholder="Datum auswählen"
                format="DD.MM.YYYY"
                value={value ? dayjs(value, 'DD.MM.YYYY') : null}
                locale={locale}
                onChange={date => handleDateChange(date, record, key)}
            />
        </div>
    );

    const columns: ColumnsType<OGLCode> = [
        { title: 'OGL', dataIndex: 'ogl', key: 'ogl', sorter: (a, b) => a.ogl.localeCompare(b.ogl) },
        { title: 'Beschreibung', dataIndex: 'description', key: 'description', sorter: (a, b) => a.description.localeCompare(b.description) },
        {
            title: 'Bezugsdatum',
            dataIndex: 'moveInDate',
            key: 'moveInDate',
            render: (value, record) => renderCell(value, record, 'moveInDate'),
        },
        {
            title: 'Gerüsttermin',
            dataIndex: 'scaffoldingDate',
            key: 'scaffoldingDate',
            render: (value, record) => renderCell(value, record, 'scaffoldingDate'),
        },
        {
            title: 'Vormontage \'Aussen\'',
            dataIndex: 'preAssemblyDateOutside',
            key: 'preAssemblyDateOutside',
            render: (value, record) => renderCell(value, record, 'preAssemblyDateOutside'),
        },
        {
            title: 'Vormontage \'Innen\'',
            dataIndex: 'preAssemblyDateInside',
            key: 'preAssemblyDateInside',
            render: (value, record) => renderCell(value, record, 'preAssemblyDateInside'),
        },
        {
            title: 'Messbereit \'Innen\'',
            dataIndex: 'measureInsideDate',
            key: 'measureInsideDate',
            render: (value, record) => renderCell(value, record, 'measureInsideDate'),
        },
        {
            title: 'Messbereit \'Aussen\'',
            dataIndex: 'measureOutsideDate',
            key: 'measureOutsideDate',
            render: (value, record) => renderCell(value, record, 'measureOutsideDate'),
        },
        {
            title: 'Messbereit \'Umgebung\'',
            dataIndex: 'measureSurroundingsDate',
            key: 'measureSurroundingsDate',
            render: (value, record) => renderCell(value, record, 'measureSurroundingsDate'),
        },
    ];

    const handleModalSave = async () => {
        if (!newOGL.ogl || !newOGL.description) {
            message.error("Bitte fülle OGL und Beschreibung aus.");
            return;
        }
        try {
            setIsSavingNewOGL(true); // Blockiere den Button
            await axios.post(`${API_BASE_URL}/createNewOGLForNetworkPlan`, {
                projectNo: activeProject,
                oglCodes: [newOGL],
            });
            setIsModalVisible(false);
            await fetchData();
        } catch (error) {
            console.error("Fehler beim Erstellen des OGL:", error);
            message.error("Fehler beim Erstellen des OGL.");
        } finally {
            setIsSavingNewOGL(false); // Button wieder freigeben
        }
    };


    const handleModalCancel = () => {
        setIsModalVisible(false);
    };


    return (
        <div style={{ padding: '20px' }}>
            <Space
                style={{
                    marginBottom: 16,
                    width: '100%',
                    justifyContent: 'flex-end', // Aligns items to the right
                    display: 'flex', // Ensures the flexbox behavior applies
                }}
            >
                <Button
                    icon={<UndoOutlined />}
                    onClick={handleReset}
                    style={{ marginRight: 8 }}
                >
                    Daten neu laden
                </Button>
                {viewType !== ViewType.NEW_EXTERNAL && (
                    <Button
                        type="primary"
                        icon={<FileAddOutlined />}
                        disabled={false}
                        onClick={handleCreateNewOGL}
                    >
                        Neuen Bauabschnitt erstellen
                    </Button>
                )}
                {viewType !== ViewType.NEW_EXTERNAL && (
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        disabled={!hasChanges()}
                        onClick={handleSave}
                    >
                        Speichern
                    </Button>
                )}
            </Space>
            <Table
                columns={columns}
                dataSource={data}
                rowKey="ogl"
                pagination={{ pageSize: 100 }}
                loading={isLoading}
            />
            <Modal
                title="Neuen Bauabschnitt erstellen"
                open={isModalVisible}
                onCancel={handleModalCancel}
                footer={[
                    <Button
                        key="cancel"
                        onClick={handleModalCancel}
                        disabled={isSavingNewOGL}
                    >
                        Abbrechen
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={handleModalSave}
                        disabled={!isOGLFormValid() || isSavingNewOGL}
                        loading={isSavingNewOGL}
                    >
                        Speichern
                    </Button>
                ]}
            >
            <div style={{ marginBottom: 12 }}>
                    <label><strong>OGL<span style={{ color: 'red' }}> *</span></strong></label>
                    <Input
                        disabled={isSavingNewOGL}
                        value={newOGL.ogl}
                        onChange={e => setNewOGL({ ...newOGL, ogl: e.target.value })}
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label><strong>Beschreibung<span style={{ color: 'red' }}> *</span></strong></label>
                    <Input
                        disabled={isSavingNewOGL}
                        value={newOGL.description}
                        onChange={e => setNewOGL({ ...newOGL, description: e.target.value })}
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label><strong>Bezugsdatum<span style={{color: 'red'}}> *</span></strong></label>
                    <DatePicker
                        locale={locale}
                        disabled={isSavingNewOGL}
                        format="DD.MM.YYYY"
                        value={newOGL.moveInDate ? dayjs(newOGL.moveInDate, 'DD.MM.YYYY') : null}
                        onChange={date =>
                            setNewOGL({ ...newOGL, moveInDate: date ? date.format('DD.MM.YYYY') : null })
                        }
                        style={{ width: '100%' }}
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label><strong>Gerüsttermin<span style={{color: 'red'}}> *</span></strong></label>
                    <DatePicker
                        locale={locale}
                        disabled={isSavingNewOGL}
                        format="DD.MM.YYYY"
                        value={newOGL.scaffoldingDate ? dayjs(newOGL.scaffoldingDate, 'DD.MM.YYYY') : null}
                        onChange={date =>
                            setNewOGL({ ...newOGL, scaffoldingDate: date ? date.format('DD.MM.YYYY') : null })
                        }
                        style={{ width: '100%' }}
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label><strong>Vormontage</strong></label>
                    <DatePicker
                        locale={locale}
                        disabled={isSavingNewOGL}
                        format="DD.MM.YYYY"
                        value={newOGL.preAssemblyDateOutside ? dayjs(newOGL.preAssemblyDateOutside, 'DD.MM.YYYY') : null}
                        onChange={date =>
                            setNewOGL({ ...newOGL, preAssemblyDateOutside: date ? date.format('DD.MM.YYYY') : null })
                        }
                        style={{ width: '100%' }}
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label><strong>Messbereit 'Innen'</strong></label>
                    <DatePicker
                        locale={locale}
                        disabled={isSavingNewOGL}
                        format="DD.MM.YYYY"
                        value={newOGL.measureInsideDate ? dayjs(newOGL.measureInsideDate, 'DD.MM.YYYY') : null}
                        onChange={date =>
                            setNewOGL({ ...newOGL, measureInsideDate: date ? date.format('DD.MM.YYYY') : null })
                        }
                        style={{ width: '100%' }}
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label><strong>Messbereit 'Aussen'</strong></label>
                    <DatePicker
                        locale={locale}
                        disabled={isSavingNewOGL}
                        format="DD.MM.YYYY"
                        value={newOGL.measureOutsideDate ? dayjs(newOGL.measureOutsideDate, 'DD.MM.YYYY') : null}
                        onChange={date =>
                            setNewOGL({ ...newOGL, measureOutsideDate: date ? date.format('DD.MM.YYYY') : null })
                        }
                        style={{ width: '100%' }}
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label><strong>Messbereit 'Umgebung'</strong></label>
                    <DatePicker
                        locale={locale}
                        disabled={isSavingNewOGL}
                        format="DD.MM.YYYY"
                        value={newOGL.measureSurroundingsDate ? dayjs(newOGL.measureSurroundingsDate, 'DD.MM.YYYY') : null}
                        onChange={date =>
                            setNewOGL({ ...newOGL, measureSurroundingsDate: date ? date.format('DD.MM.YYYY') : null })
                        }
                        style={{ width: '100%' }}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default OGLConfiguration;
